<template>
  <div v-if="isOpen" class="cloud-modal-overlay">
    <div class="cloud-modal-content">
      <h3 class="cloud-modal-title">クラウドデータ管理</h3>
      
      <div class="cloud-modal-body">
        <div v-if="status === 'loading'">
          <div class="loading-indicator">
            <div class="spinner"></div>
            <div>データ取得中...</div>
          </div>
        </div>
        
        <div v-else-if="status === 'no-data'">
          <div class="no-data-message">
            <div class="info-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <p>クラウドに保存されたデータがありません。</p>
            <p class="sub-info">「クラウドに保存」からデータを保存すると、ここにクラウドデータの管理ができるようになります。</p>
          </div>
        </div>
        
        <div v-else-if="status === 'auth-required'">
          <div class="pin-auth-section">
            <p>クラウドデータへのアクセスには認証が必要です。</p>
            
            <div class="pin-input-container">
              <label for="pin-auth">PINコード (4桁)：</label>
              <input 
                type="password" 
                id="pin-auth" 
                v-model="pinCode" 
                maxlength="4" 
                pattern="[0-9]*" 
                inputmode="numeric"
                @input="validatePinCode"
                ref="pinInput"
              />
              <div v-if="pinError" class="pin-error">{{ pinError }}</div>
            </div>
            
            <div class="auth-buttons">
              <button class="cancel-button" @click="close">キャンセル</button>
              <button 
                class="auth-button" 
                @click="authenticate" 
                :disabled="pinCode.length !== 4 || isProcessing"
              >
                {{ isProcessing ? '認証中...' : '認証' }}
              </button>
            </div>
          </div>
        </div>
        
        <div v-else-if="status === 'data-found'">
          <div class="cloud-data-details">
            <div class="data-info-item">
              <div class="info-label">最終保存日時：</div>
              <div class="info-value">{{ cloudData.savedAt }}</div>
            </div>
            <div class="data-info-item">
              <div class="info-label">最終アクセス日時：</div>
              <div class="info-value">{{ cloudData.lastAccessed }}</div>
            </div>
            <div class="data-info-item">
              <div class="info-label">ユニークURL：</div>
              <div class="info-value url-value">
                {{ cloudData.url }}
                <button class="copy-url-button" @click="copyUrl">
                  {{ urlCopied ? 'コピー完了' : 'コピー' }}
                </button>
              </div>
            </div>
          </div>
          
          <div class="cloud-danger-zone">
            <div class="danger-zone-title">危険な操作</div>
            <p class="danger-zone-desc">クラウドデータを削除すると、すべてのデバイスから参照できなくなります。この操作は元に戻せません。</p>
            
            <div v-if="deleteConfirm">
              <div class="delete-confirm">
                <p>本当にクラウドデータを削除しますか？</p>
                <div class="confirm-pin">
                  <label for="confirm-pin">確認のためPINコードを入力：</label>
                  <input 
                    type="password" 
                    id="confirm-pin" 
                    v-model="confirmDeletePin" 
                    maxlength="4" 
                    pattern="[0-9]*" 
                    inputmode="numeric"
                    ref="confirmPinInput"
                  />
                  <div v-if="deleteError" class="pin-error">{{ deleteError }}</div>
                </div>
                <div class="delete-confirm-buttons">
                  <button class="cancel-delete" @click="cancelDelete">キャンセル</button>
                  <button 
                    class="confirm-delete" 
                    @click="confirmDeleteAction" 
                    :disabled="confirmDeletePin.length !== 4 || isDeleteProcessing"
                  >
                    {{ isDeleteProcessing ? '処理中...' : '削除を確定' }}
                  </button>
                </div>
              </div>
            </div>
            <div v-else>
              <button class="delete-button" @click="startDelete">クラウドデータを削除する</button>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="status !== 'auth-required' && status !== 'loading'" class="cloud-modal-footer">
        <button class="close-button" @click="close">閉じる</button>
      </div>
    </div>
    <!-- ネイティブalert代替トースト -->
    <v-snackbar v-model="snack.show" :color="snack.color" :timeout="4000" location="bottom">
      {{ snack.text }}
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'CloudDataModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      status: 'loading', // loading, no-data, auth-required, data-found
      pinCode: '',
      pinError: '',
      isProcessing: false,
      cloudData: {
        savedAt: '',
        lastAccessed: '',
        url: ''
      },
      urlCopied: false,
      deleteConfirm: false,
      confirmDeletePin: '',
      deleteError: '',
      isDeleteProcessing: false,
      // ネイティブalert代替トースト
      snack: { show: false, text: '', color: 'error' }
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.status = 'loading';
        this.pinCode = '';
        this.pinError = '';
        this.isProcessing = false;
        this.urlCopied = false;
        this.deleteConfirm = false;
        this.confirmDeletePin = '';
        this.deleteError = '';
        this.isDeleteProcessing = false;
        
        // リアルなシナリオでは、ここでクラウドデータの状態を確認するAPIを呼び出す
        this.checkCloudData();
        
        // PINコード入力フィールドへのフォーカス設定（auth-required時）
        this.$nextTick(() => {
          if (this.status === 'auth-required' && this.$refs.pinInput) {
            this.$refs.pinInput.focus();
          }
        });
      }
    }
  },
  methods: {
    showSnack(text, color = 'error') {
      this.snack.text = text;
      this.snack.color = color;
      this.snack.show = true;
    },

    checkCloudData() {
      // 本番では実際のAPIコールになりますが、ローカルテストのためにモック処理
      setTimeout(() => {
        // ユーザーのクラウドデータの状態を確認
        // ここでは、クッキーからユニークURLがあるかをチェック
        const cloudUrl = this.getCookie('bkms_cloud_url');
        
        if (cloudUrl) {
          // 認証状態を確認
          const isAuthenticated = this.getCookie('bkms_cloud_auth') === 'true';
          
          if (isAuthenticated) {
            // 認証済みならデータ詳細を取得
            this.getCloudDataDetails(cloudUrl);
          } else {
            // 認証が必要
            this.status = 'auth-required';
          }
        } else {
          // クラウドデータがない
          this.status = 'no-data';
        }
      }, 800);
    },
    
    getCloudDataDetails(cloudUrl) {
      // 本番では実際のAPIコールになりますが、ローカルテストのためにモック処理
      setTimeout(() => {
        // データが見つかった場合のモックデータ
        this.cloudData = {
          savedAt: new Date().toLocaleString('ja-JP'),
          lastAccessed: new Date().toLocaleString('ja-JP'),
          url: cloudUrl || 'https://bkms.txt365.org/' + this.generateRandomId()
        };
        this.status = 'data-found';
      }, 500);
    },
    
    validatePinCode() {
      // 数字のみかチェック
      if (!/^\d*$/.test(this.pinCode)) {
        this.pinCode = this.pinCode.replace(/[^\d]/g, '');
      }
      
      if (this.pinCode.length === 0) {
        this.pinError = 'PINコードを入力してください';
      } else if (this.pinCode.length !== 4) {
        this.pinError = 'PINコードは4桁の数字で入力してください';
      } else {
        this.pinError = '';
      }
    },
    
    authenticate() {
      if (this.pinCode.length !== 4) return;
      
      this.isProcessing = true;
      
      // 本番環境では実際にPINコードを使った認証を行う
      setTimeout(() => {
        // テスト用の処理：PINコード認証に成功したと仮定
        const cloudUrl = this.getCookie('bkms_cloud_url') || 'https://bkms.txt365.org/' + this.generateRandomId();
        
        // 認証成功を記録
        this.setCookie('bkms_cloud_auth', 'true', 30); // 30日間有効
        
        // データ詳細を取得
        this.getCloudDataDetails(cloudUrl);
        
        this.isProcessing = false;
      }, 1000);
    },
    
    startDelete() {
      this.deleteConfirm = true;
      this.confirmDeletePin = '';
      this.deleteError = '';
      
      // 次のティックでPINコード入力欄にフォーカスを当てる
      this.$nextTick(() => {
        if (this.$refs.confirmPinInput) {
          this.$refs.confirmPinInput.focus();
        }
      });
    },
    
    cancelDelete() {
      this.deleteConfirm = false;
      this.confirmDeletePin = '';
      this.deleteError = '';
    },
    
    confirmDeleteAction() {
      if (this.confirmDeletePin.length !== 4) {
        this.deleteError = 'PINコードを入力してください';
        return;
      }
      
      this.isDeleteProcessing = true;

      // クラウドIDを取得（ユニークURLからIDを抽出または直接localStorage/Cookieから取得）
      let cloudId = localStorage.getItem('bkms_cloud_id');
      
      if (!cloudId) {
        // URLからIDを抽出する試み
        const cloudUrl = this.getCookie('bkms_cloud_url');
        if (cloudUrl) {
          const urlParts = cloudUrl.split('/');
          if (urlParts.length > 0) {
            cloudId = urlParts[urlParts.length - 1];
          }
        }
      }
      
      if (!cloudId) {
        this.deleteError = 'クラウドIDが見つかりません';
        this.isDeleteProcessing = false;
        return;
      }
      
      // APIエンドポイントを構築
      const apiEndpoint = `https://bkms-worker.jajajunk.workers.dev/api/delete/${cloudId}`;
      
      console.log('クラウドデータ削除リクエストを送信:', {
        endpoint: apiEndpoint,
        id: cloudId,
        pinLength: this.confirmDeletePin.length
      });

      // APIを呼び出してデータを削除
      fetch(apiEndpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ pin: this.confirmDeletePin }),
        mode: 'cors'
      })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            let errorData;
            try {
              errorData = JSON.parse(text);
            } catch (e) {
              errorData = { error: text };
            }
            throw new Error(`削除エラー: ${errorData.error || 'サーバーからのレスポンスエラー'}`);
          });
        }
        return response.json();
      })
      .then(data => {
        console.log('クラウドデータ削除成功:', data);
        
        // Cookieから認証情報と保存URLを削除
        this.deleteCookie('bkms_cloud_url');
        this.deleteCookie('bkms_cloud_auth');
        this.deleteCookie('bkms_cloud_pin');
        
        // ローカルストレージからクラウド関連情報を削除
        localStorage.removeItem('bkms_cloud_saved_at');
        localStorage.removeItem('bkms_cloud_id');
        localStorage.removeItem('bkms_last_cloud_access');
        
        // 削除イベントを発行
        this.$emit('delete');
        
        // モーダルを閉じる
        this.close();
        
        // 処理中状態を解除
        this.isDeleteProcessing = false;
        
        // ルートURLへリダイレクト
        const rootUrl = window.location.origin;
        console.log('ルートURLへリダイレクトします:', rootUrl);
        window.location.href = rootUrl;
      })
      .catch(error => {
        console.error('クラウドデータ削除エラー:', error);
        this.deleteError = error.message || 'データの削除に失敗しました。PINコードが正しいか確認してください。';
        this.isDeleteProcessing = false;
      });
    },
    
    copyUrl() {
      if (!this.cloudData.url) return;
      
      try {
        navigator.clipboard.writeText(this.cloudData.url).then(() => {
          this.urlCopied = true;
          
          // 2秒後に状態をリセット
          setTimeout(() => {
            this.urlCopied = false;
          }, 2000);
        });
      } catch (error) {
        console.error('クリップボードへのコピーに失敗しました', error);
        this.showSnack('URLのコピーに失敗しました。手動でコピーしてください。');
      }
    },
    
    close() {
      this.$emit('close');
    },
    
    // ユーティリティメソッド
    getCookie(name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      if (match) {
        const value = decodeURIComponent(match[2]);
        // アクセスのたびに期限を延長
        this.extendCookieExpiry(name, value);
        return value;
      }
      return '';
    },
    
    setCookie(name, value, days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = "expires=" + date.toUTCString();
      document.cookie = name + "=" + encodeURIComponent(value) + ";" + expires + ";path=/";
    },

    // Cookie期限延長処理
    extendCookieExpiry(name, value) {
      // 期限延長対象のcookieと期間を定義
      const cookieSettings = {
        'bkms_cloud_auth': 30,        // PINコード認証状態
        'bkms_cloud_pin': 30,         // PINコード
        'bkms_cloud_url': 365,        // クラウドURL
        'bkms_terms_agreed': 365      // 利用規約同意
      };

      // 対象cookieの場合のみ期限を延長
      if (cookieSettings[name]) {
        const daysToExpire = cookieSettings[name];
        const date = new Date();
        date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
        const expires = "expires=" + date.toUTCString();
        document.cookie = name + "=" + encodeURIComponent(value) + ";" + expires + ";path=/";
        console.log(`[CloudDataModal Cookie延長] ${name}の期限を${daysToExpire}日間延長しました`);
      }
    },
    
    deleteCookie(name) {
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;";
    },
    
    generateRandomId() {
      const bytes = new Uint8Array(7);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').substring(0, 9);
    }
  }
}
</script>

<style scoped>
.cloud-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
}

.cloud-modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 550px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.cloud-modal-title {
  color: #333;
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  color: #666;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #e9d0ca;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data-message {
  text-align: center;
  padding: 30px 10px;
  color: #666;
}

.info-icon {
  color: #e9d0ca;
  margin-bottom: 15px;
}

.sub-info {
  font-size: 0.9rem;
  margin-top: 10px;
  color: #888;
}

.pin-auth-section {
  padding: 10px 0;
}

.pin-input-container {
  margin: 15px 0;
}

.pin-input-container label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #444;
}

.pin-input-container input,
.confirm-pin input {
  width: 100%;
  padding: 10px;
  font-size: 1.1rem;
  letter-spacing: 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
  margin: 8px 0;
  color: #666; /* テキスト色を#666に設定 */
  background-color: white;
}

.pin-input-container input:focus,
.confirm-pin input:focus {
  border-color: #e9d0ca;
  outline: none;
  box-shadow: 0 0 0 3px rgba(233, 208, 202, 0.25);
}

/* ダークモードでの入力フィールドのスタイル */
:global(.v-theme--dark) .pin-input-container input,
:global(.v-theme--dark) .confirm-pin input {
  background-color: #2d2d2d;
  border-color: #555;
  color: #666; /* ダークモードでも文字色を#666に保持 */
}

.pin-error {
  color: #d9534f;
  font-size: 0.85rem;
  margin-top: 4px;
}

.auth-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.cancel-button {
  background-color: #f5f5f5;
  color: #333;
  border: none;
  padding: 10px 18px;
  border-radius: 4px;
  margin-right: 10px;
  cursor: pointer;
  font-weight: 500;
}

.auth-button {
  background-color: #e9d0ca;
  color: #333;
  border: none;
  padding: 10px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.auth-button:hover {
  background-color: #e0c0b8;
}

.auth-button:disabled {
  background-color: #e9d0ca;
  opacity: 0.6;
  cursor: not-allowed;
}

.cloud-data-details {
  margin-bottom: 25px;
}

.data-info-item {
  display: flex;
  margin-bottom: 12px;
  align-items: flex-start;
}

.info-label {
  width: 140px;
  flex-shrink: 0;
  font-weight: 500;
  color: #555;
}

.info-value {
  flex: 1;
}

.url-value {
  display: flex;
  align-items: center;
  word-break: break-all;
}

.copy-url-button {
  background-color: #e9d0ca;
  border: none;
  color: #333;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
  font-size: 0.8rem;
  white-space: nowrap;
}

.cloud-danger-zone {
  margin-top: 30px;
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.danger-zone-title {
  color: #d9534f;
  font-weight: 700;
  margin-bottom: 10px;
}

.danger-zone-desc {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 15px;
}

.delete-button {
  background-color: #d9534f;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.delete-button:hover {
  background-color: #c9302c;
}

.delete-confirm {
  background-color: #fff8f8;
  border: 1px solid #f5d0d0;
  border-radius: 4px;
  padding: 15px;
}

/* ダークモードでの削除確認エリアのスタイル */
:global(.v-theme--dark) .delete-confirm {
  background-color: #3a2a2a;
  border-color: #5a3a3a;
}

.confirm-pin {
  margin: 15px 0;
}

.confirm-pin label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #444;
}

/* ダークモードでのラベルスタイル */
:global(.v-theme--dark) .confirm-pin label,
:global(.v-theme--dark) .pin-input-container label {
  color: #aaa;
}

.delete-confirm-buttons {
  display: flex;
  justify-content: flex-end;
}

.cancel-delete {
  background-color: #f5f5f5;
  color: #333;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  margin-right: 10px;
  cursor: pointer;
}

.confirm-delete {
  background-color: #d9534f;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.confirm-delete:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cloud-modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.close-button {
  background-color: #e9d0ca;
  color: #333;
  border: none;
  padding: 10px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #e0c0b8;
}

/* ダークモードでのモーダルコンテンツのスタイル */
:global(.v-theme--dark) .cloud-modal-content {
  background-color: #1e1e1e;
  color: #666;
}

:global(.v-theme--dark) .cloud-modal-title {
  color: #aaa;
  border-bottom-color: #333;
}

:global(.v-theme--dark) .cloud-danger-zone {
  border-top-color: #333;
}

@media (max-width: 600px) {
  .cloud-modal-content {
    width: 95%;
    padding: 15px;
  }
  
  .info-label {
    width: 120px;
    font-size: 0.9rem;
  }
  
  .info-value {
    font-size: 0.9rem;
  }
  
  .data-info-item {
    margin-bottom: 10px;
  }
}
</style>
